/**
 * Customer Billing Portal — production billing for admins.
 * Premium bright design system (dashboard-premium.css).
 *
 * Sections:
 *   1. Billing identity   — country, currency, address, tax id (GSTIN/VAT/EIN/HST).
 *   2. Payment methods    — list, default, fallback, attach (Stripe Checkout), detach.
 *   3. Subscription       — current plan, seats, auto-debit, change plan, cancel/reactivate.
 *   4. Invoices           — history + download PDF.
 *
 * Routing — `/app/billing`, admin only. Owner uses the owner billing console.
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EmptyState from "@/components/EmptyState";
import DataTable from "@/components/DataTable";
import "./dashboard-premium.css";
import {
  CreditCard, Receipt, Globe, Star, ArrowsClockwise, Trash, CheckCircle,
  Warning, PaperPlaneTilt, Download, Plus, ListChecks, ShieldCheck,
} from "@phosphor-icons/react";

const TAX_ID_LABEL = {
  in_gst: "GSTIN",
  gb_vat: "UK VAT",
  eu_vat: "EU VAT",
  ca_gst_hst: "Canada GST/HST",
  ca_qst: "QST",
  us_ein: "US EIN",
};

const fmt = (minor, currency) => {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "USD").toUpperCase(), maximumFractionDigits: 0 }).format(minor / 100);
};

export default function Billing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [customer, setCustomer] = useState(null);
  const [methods, setMethods] = useState(null);
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [plans, setPlans] = useState(null);
  const [err, setErr] = useState("");
  const [staleNotices, setStaleNotices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");

  const reloadAll = async () => {
    setErr("");
    setStaleNotices([]);
    const paths = [
      ["/billing/v2/customer",         (d) => setCustomer(d),                             "customer profile"],
      ["/billing/v2/payment-methods",  (d) => setMethods(d),                            "payment methods"],
      ["/billing/v2/subscription",     (d) => setSub(d?.subscription),                  "subscription"],
      ["/billing/v2/invoices",         (d) => setInvoices(d?.invoices || []),           "invoices"],
      ["/billing/v2/plans",            (d) => setPlans(d),                                "plan catalog"],
    ];
    const results = await Promise.allSettled(paths.map(([p]) => api.get(p)));
    const failures = [];
    const stale = [];
    results.forEach((r, i) => {
      const [path, setter, label] = paths[i];
      if (r.status === "fulfilled") {
        setter(r.value.data);
        // Backend flagged this response as served from the last-known
        // snapshot because Stripe was slow / unreachable. Surface a soft
        // notice instead of a scary red banner.
        if (r.value.data && r.value.data.stale) {
          stale.push({
            label,
            last: r.value.data.last_synced_at,
            reason: r.value.data.reason,
          });
        }
      } else {
        const detail = r.reason?.response?.data?.detail;
        const status = r.reason?.response?.status;
        failures.push(`${label} (${status || "network"}): ${formatApiError(detail) || r.reason?.message || "unknown"}`);
        // eslint-disable-next-line no-console
        console.error("[billing]", path, r.reason);
      }
    });
    if (failures.length) {
      setErr(failures.join(" · "));
    }
    if (stale.length) {
      setStaleNotices(stale);
    }
  };
  useEffect(() => { reloadAll(); }, []);

  // Returning from Stripe Checkout?
  useEffect(() => {
    const flag = sp.get("billing");
    if (flag === "success") { setToast("Subscription updated — give it a few seconds for the webhook to sync."); reloadAll(); }
    if (flag === "cancelled") { setToast("Checkout cancelled."); }
  }, [sp]);

  if (user?.role !== "admin" && user?.role !== "owner") {
    return <div className="p-10" data-testid="billing-forbidden">Only an admin can access billing.</div>;
  }

  return (
    <div className="hbf-premium-scope px-8 py-8 md:px-12" data-testid="billing-page">
      <header className="hd-hero hd-fade-up max-w-[1280px]">
        <div>
          <div className="hd-hero__eyebrow">Billing</div>
          <h1 className="hd-hero__title">Plans, payments &amp; <span className="hd-hero__title-accent">invoices</span></h1>
          <p className="hd-hero__sub">Powered by Stripe · multi-currency · tax handled automatically.</p>
        </div>
      </header>

      <div className="max-w-[1280px]">
        {toast && <div className="hd-notice hd-notice--success" data-testid="billing-toast"><CheckCircle size={16} className="shrink-0 mt-0.5"/><span>{toast}</span></div>}
        {err && <div className="hd-notice hd-notice--danger" data-testid="billing-error"><Warning size={16} className="shrink-0 mt-0.5"/><span>{err}</span></div>}
        {staleNotices.length > 0 && (
          <div className="hd-notice" data-testid="billing-stale">
            <ArrowsClockwise size={16} className="shrink-0 mt-0.5"/>
            <span>
              Showing the last known {staleNotices.map((s) => s.label).join(" & ")} — Stripe is briefly unreachable.
              {" "}Data will refresh automatically once the connection recovers.
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <IdentityCard customer={customer} plans={plans} onSaved={reloadAll} setBusy={setBusy} busy={busy}/>
          <SubscriptionCard sub={sub} plans={plans} onChanged={reloadAll} busy={busy} setBusy={setBusy}/>
        </div>

        <PaymentMethodsCard methods={methods} customer={customer} onChanged={reloadAll} busy={busy} setBusy={setBusy} setToast={setToast}/>
        <InvoicesCard invoices={invoices}/>
        <PlanGrid plans={plans} sub={sub} onChanged={reloadAll} busy={busy} setBusy={setBusy} customer={customer} nav={nav}/>
      </div>
    </div>
  );
}

/* ============================================================
 *  Identity (address + tax id)
 * ============================================================ */
function IdentityCard({ customer, plans, onSaved, setBusy, busy }) {
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (!customer) return;
    setForm({
      line1: customer.billing_address?.line1 || "",
      line2: customer.billing_address?.line2 || "",
      city:  customer.billing_address?.city  || "",
      state: customer.billing_address?.state || "",
      postal_code: customer.billing_address?.postal_code || "",
      country: customer.billing_address?.country || "US",
      tax_id_type:  customer.tax_id?.type || "",
      tax_id_value: customer.tax_id?.value || "",
    });
  }, [customer]);
  if (!customer || !form || !plans) return <Skeleton title="Billing identity"/>;

  const supported = plans.country.tax_id_types || [];
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = {
        address: { line1: form.line1, line2: form.line2 || null, city: form.city, state: form.state || null, postal_code: form.postal_code, country: form.country },
      };
      if (form.tax_id_type && form.tax_id_value) body.tax_id = { type: form.tax_id_type, value: form.tax_id_value };
      await api.post("/billing/v2/customer", body);
      await onSaved();
    } catch (e2) { alert(formatApiError(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="hd-card hd-card--static hd-fade-up hd-stagger-1 p-6" data-testid="identity-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="hd-panel__eyebrow flex items-center gap-1.5"><Globe size={11}/> Billing identity</div>
          <div className="hd-panel__title mt-1">Address &amp; tax ID</div>
        </div>
        <span className="hd-pill hd-pill--no-answer">{plans.country.currency.toUpperCase()} · {plans.country.tax_mode.toUpperCase()}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><label className="label">Address line 1</label><input className="input" required value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} data-testid="addr-line1"/></div>
        <div className="col-span-2"><label className="label">Line 2 (optional)</label><input className="input" value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })}/></div>
        <div><label className="label">City</label><input className="input" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}/></div>
        <div><label className="label">State / region</label><input className="input" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}/></div>
        <div><label className="label">Postal code</label><input className="input" required value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })}/></div>
        <div>
          <label className="label">Country</label>
          <select className="input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="addr-country">
            <option value="US">United States</option>
            <option value="CA">Canada</option>
            <option value="GB">United Kingdom</option>
            <option value="IN">India</option>
          </select>
        </div>
        <div>
          <label className="label">Tax ID type (optional)</label>
          <select className="input" value={form.tax_id_type} onChange={(e) => setForm({ ...form, tax_id_type: e.target.value })} data-testid="tax-id-type">
            <option value="">—</option>
            {supported.map((t) => <option key={t} value={t}>{TAX_ID_LABEL[t] || t.toUpperCase()}</option>)}
          </select>
        </div>
        <div><label className="label">Tax ID value</label><input className="input" value={form.tax_id_value} onChange={(e) => setForm({ ...form, tax_id_value: e.target.value })} placeholder="GSTIN / VAT / EIN…" data-testid="tax-id-value"/></div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="hd-panel__eyebrow !mt-0 truncate">{customer.customer_id ? `Stripe customer · ${customer.customer_id}` : "Stripe customer not yet created"}</div>
        <button type="submit" disabled={busy} className="hd-btn hd-btn--primary shrink-0" data-testid="save-identity">{busy ? "Saving…" : "Save identity"}</button>
      </div>
    </form>
  );
}

/* ============================================================
 *  Subscription
 * ============================================================ */
function SubscriptionCard({ sub, plans, onChanged, busy, setBusy }) {
  if (!plans) return <Skeleton title="Subscription"/>;
  const cur = plans.country.currency;
  const planRow = sub ? plans.plans.find((p) => p.key === sub.plan_key) : null;

  const cancel = async () => {
    if (!window.confirm("Cancel at period end? You'll keep access until then.")) return;
    setBusy(true);
    try { await api.post("/billing/v2/subscription/cancel"); await onChanged(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const reactivate = async () => {
    setBusy(true);
    try { await api.post("/billing/v2/subscription/reactivate"); await onChanged(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const toggleAutoDebit = async (enabled) => {
    setBusy(true);
    try { await api.post("/billing/v2/subscription/auto-debit", { enabled }); await onChanged(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="hd-card hd-card--static hd-fade-up hd-stagger-2 p-6" data-testid="subscription-card">
      <div className="hd-panel__eyebrow flex items-center gap-1.5"><Receipt size={11}/> Subscription</div>
      <div className="hd-panel__title mt-1 mb-4">Plan, auto-debit &amp; lifecycle</div>
      {!sub ? (
        <div className="text-[13px] text-[var(--hd-text-muted)] py-8 border border-dashed border-[var(--hd-border-strong)] rounded-xl text-center bg-[var(--hd-surface-soft)]" data-testid="no-sub">
          No active subscription. Pick a plan below to start.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field k="Plan" v={planRow?.name || sub.plan_key || "—"}/>
            <Field k="Seats" v={sub.quantity ?? "—"}/>
            <Field k="Status" v={<span className={`hd-pill ${sub.status === "active" ? "hd-pill--completed" : "hd-pill--inprogress"}`}>{sub.status}</span>}/>
            <Field k="Renews" v={sub.current_period_end ? new Date(sub.current_period_end * 1000).toLocaleDateString() : "—"}/>
            <Field k="Monthly" v={planRow?.amount_minor ? fmt(planRow.amount_minor * (sub.quantity || 1), cur) : "—"}/>
            <Field k="Auto-debit" v={sub.cancel_at_period_end ? "OFF (cancels at period end)" : "ON"}/>
          </div>
          <div className="flex gap-2 pt-1 flex-wrap">
            {sub.cancel_at_period_end ? (
              <button onClick={reactivate} disabled={busy} className="hd-btn hd-btn--primary" data-testid="reactivate"><ArrowsClockwise size={13}/> Reactivate</button>
            ) : (
              <>
                <button onClick={() => toggleAutoDebit(false)} disabled={busy} className="hd-btn hd-btn--ghost" data-testid="autodebit-off">Disable auto-debit</button>
                <button onClick={cancel} disabled={busy} className="hd-btn hd-btn--ghost !text-rose-700" data-testid="cancel-sub">Cancel subscription</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ k, v }) => (
  <div className="p-3 border border-[var(--hd-border-strong)] rounded-xl bg-[var(--hd-surface-soft)]/60">
    <div className="hd-panel__eyebrow !mt-0">{k}</div>
    <div className="text-[14px] font-semibold mt-1">{v}</div>
  </div>
);

const Skeleton = ({ title }) => (
  <div className="hd-card hd-card--static p-6">
    <div className="hd-panel__eyebrow">{title}</div>
    <div className="mt-4 space-y-3">
      <div className="hd-skel h-4 w-2/3"/>
      <div className="hd-skel h-4 w-1/2"/>
      <div className="hd-skel h-4 w-3/4"/>
    </div>
  </div>
);

/* ============================================================
 *  Payment methods
 * ============================================================ */
function PaymentMethodsCard({ methods, customer, onChanged, busy, setBusy, setToast }) {
  if (!methods) return null;
  const ready = methods.provider_ready;

  const addMethod = async () => {
    // Prefer the Stripe Customer Portal — the canonical UX for card management.
    // If it fails (e.g. no customer yet), fall back to nudging them to Checkout.
    try {
      setBusy(true);
      const { data } = await api.post("/billing/v2/portal");
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
    } catch {
      setToast("Pick a plan below to start checkout — Stripe will collect your card during the flow.");
      document.getElementById("plan-grid")?.scrollIntoView({ behavior: "smooth" });
    } finally { setBusy(false); }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/v2/portal");
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const setDefault = async (pm) => {
    setBusy(true);
    try { await api.post(`/billing/v2/payment-methods/${pm}/default`); await onChanged(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const setFallback = async (pm) => {
    setBusy(true);
    try { await api.post(`/billing/v2/payment-methods/${pm}/fallback`); await onChanged(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const detach = async (pm) => {
    if (!window.confirm("Remove this payment method?")) return;
    setBusy(true);
    try { await api.delete(`/billing/v2/payment-methods/${pm}`); await onChanged(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="hd-card hd-card--static hd-fade-up hd-stagger-3 p-6 mt-6" data-testid="payment-methods-card">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="hd-panel__eyebrow flex items-center gap-1.5"><CreditCard size={11}/> Payment methods</div>
          <div className="hd-panel__title mt-1">Saved cards — default + fallback</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={openPortal} disabled={!ready || busy}
                  className="hd-btn hd-btn--secondary"
                  title="Open the Stripe-hosted Customer Portal to manage cards, invoices, and cancellations"
                  data-testid="open-portal-btn">
            Manage Billing
          </button>
          <button onClick={addMethod} disabled={!ready || busy} className="hd-btn hd-btn--primary" data-testid="add-pm-btn"><Plus size={13}/> Add card</button>
        </div>
      </div>

      {!ready ? (
        <div className="text-[13px] text-[var(--hd-text-muted)] py-8 border border-dashed border-[var(--hd-border-strong)] rounded-xl text-center bg-[var(--hd-surface-soft)]" data-testid="no-customer">Save your billing identity above first.</div>
      ) : methods.methods.length === 0 ? (
        <div className="text-[13px] text-[var(--hd-text-muted)] py-8 border border-dashed border-[var(--hd-border-strong)] rounded-xl text-center bg-[var(--hd-surface-soft)]" data-testid="no-methods">No saved methods yet — click &ldquo;Add card&rdquo; or start checkout.</div>
      ) : (
        <DataTable
          testId="pm-table"
          data={methods.methods}
          rowKey={(m) => m.id}
          rowTestId={(m) => `pm-${m.id}`}
          pageSize={10}
          columns={[
            { key: "brand", label: "Card", sortable: true, render: (m) => <span className="font-mono text-[13px]">{m.brand?.toUpperCase()} •••• {m.last4}</span> },
            { key: "exp_year", label: "Exp", sortable: true, width: "80px", render: (m) => <span className="font-mono text-[12px]">{String(m.exp_month).padStart(2, "0")}/{String(m.exp_year).slice(-2)}</span> },
            { key: "is_default",  label: "Default",  width: "150px", render: (m) => (m.is_default ? <span className="hd-pill hd-pill--completed"><CheckCircle size={10} weight="fill"/> DEFAULT</span> : <button onClick={() => setDefault(m.id)} className="hd-btn hd-btn--ghost !py-1 !px-2.5 !text-[11px]" data-testid={`make-default-${m.id}`}>Make default</button>) },
            { key: "is_fallback", label: "Fallback", width: "150px", render: (m) => (m.is_fallback ? <span className="hd-pill hd-pill--inbound"><Star size={10} weight="fill"/> FALLBACK</span> : <button onClick={() => setFallback(m.id)} className="hd-btn hd-btn--ghost !py-1 !px-2.5 !text-[11px]" data-testid={`make-fallback-${m.id}`}>Make fallback</button>) },
            { key: "_act", label: "", width: "60px", render: (m) => <button onClick={() => detach(m.id)} className="p-1.5 rounded-lg border border-[var(--hd-border-strong)] hover:bg-red-50 hover:border-[var(--hd-danger)] text-[var(--hd-danger)] transition-colors" title="Remove" data-testid={`detach-${m.id}`}><Trash size={13}/></button> },
          ]}
        />
      )}
      <div className="mt-4 hd-panel__eyebrow !mt-4 flex items-center gap-1.5"><ShieldCheck size={12}/> Card data is stored only by Stripe (PCI-DSS Level 1). We persist tokens only.</div>
    </div>
  );
}

/* ============================================================
 *  Invoices
 * ============================================================ */
function InvoicesCard({ invoices }) {
  if (!invoices) return null;
  const download = async (id, number) => {
    try {
      const r = await api.get(`/billing/v2/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `invoice-${number || id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="hd-card hd-card--static hd-fade-up hd-stagger-4 p-6 mt-6" data-testid="invoices-card">
      <div className="hd-panel__eyebrow flex items-center gap-1.5"><ListChecks size={11}/> Invoices</div>
      <div className="hd-panel__title mt-1 mb-4">Tax-compliant invoices with subtotal, tax &amp; total</div>
      {invoices.length === 0 ? (
        <EmptyState
          icon="billing"
          title="No invoices yet"
          description="Invoices appear here at the end of each billing cycle. They include subtotal, tax, and a downloadable PDF."
          learnMore={{ label: "How billing works", href: "/legal/terms" }}
          compact
          testId="empty-state-invoices"
        />
      ) : (
        <DataTable
          testId="invoices-table"
          data={invoices}
          rowKey={(i) => i.id}
          rowTestId={(i) => `invoice-${i.id}`}
          searchPlaceholder="Search invoices…"
          searchKeys={["number", "id", "status"]}
          columns={[
            { key: "number",  label: "Number", sortable: true, render: (i) => <span className="font-mono text-[12px]">{i.number || i.id.slice(0,12)}</span> },
            { key: "created", label: "Date",   sortable: true, width: "130px", render: (i) => <span className="font-mono text-[12px] text-[var(--hd-text-muted)]">{new Date(i.created * 1000).toLocaleDateString()}</span> },
            { key: "status",  label: "Status", sortable: true, width: "100px", render: (i) => <span className={`hd-pill ${i.status === "paid" ? "hd-pill--completed" : "hd-pill--inprogress"}`}>{i.status?.toUpperCase()}</span> },
            { key: "subtotal", label: "Subtotal", sortable: true, width: "110px", align: "right", render: (i) => <span className="tabular-nums">{fmt(i.subtotal, i.currency)}</span> },
            { key: "tax",      label: "Tax",      sortable: true, width: "90px",  align: "right", render: (i) => <span className="tabular-nums">{fmt(i.tax, i.currency)}</span> },
            { key: "total",    label: "Total",    sortable: true, width: "110px", align: "right", render: (i) => <span className="tabular-nums font-semibold">{fmt(i.total, i.currency)}</span> },
            { key: "_act", label: "", width: "60px", render: (i) => <button onClick={() => download(i.id, i.number)} className="p-1.5 rounded-lg border border-[var(--hd-border-strong)] hover:bg-[var(--hd-surface-soft)] transition-colors" data-testid={`dl-invoice-${i.id}`}><Download size={13}/></button> },
          ]}
        />
      )}
    </div>
  );
}

/* ============================================================
 *  Plan grid (start checkout / change plan)
 * ============================================================ */
function PlanGrid({ plans, sub, onChanged, busy, setBusy, customer, nav }) {
  if (!plans || !customer) return null;
  const cur = plans.country.currency;

  const startCheckout = async (planKey, seats = 1) => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/v2/checkout", { plan_key: planKey, seats });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  const change = async (planKey) => {
    if (!window.confirm(`Switch to ${planKey.toUpperCase()}? Prorations apply.`)) return;
    setBusy(true);
    try { await api.post("/billing/v2/subscription/change-plan", { plan_key: planKey }); await onChanged(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div id="plan-grid" className="hd-card hd-card--static hd-fade-up p-6 mt-6" data-testid="plan-grid">
      <div className="hd-panel__eyebrow">Plans</div>
      <div className="hd-panel__title mt-1">Per-seat pricing in {cur.toUpperCase()}</div>
      <div className="text-[12px] text-[var(--hd-text-muted)] mt-1 mb-5">
        {plans.country.tax_mode === "inclusive" ? "Prices include applicable tax (VAT / GST)." : "Prices exclude tax. Stripe Tax will compute it at checkout."}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.plans.map((p) => {
          const isCurrent = sub?.plan_key === p.key;
          return (
            <div key={p.key}
                 className={`relative rounded-2xl border p-5 flex flex-col transition-transform duration-200 hover:-translate-y-1 hover:shadow-[var(--hd-shadow-lg)] ${isCurrent ? "border-[var(--hd-accent)] shadow-[inset_0_0_0_1px_rgba(37,99,235,.35),0_8px_24px_rgba(37,99,235,.12)] bg-gradient-to-b from-[rgba(37,99,235,.05)] to-white" : "border-[var(--hd-border-strong)] bg-gradient-to-b from-white to-[#F8FAFC] shadow-[var(--hd-shadow-sm)]"}`}
                 data-testid={`plan-card-${p.key}`}>
              {isCurrent && (
                <span className="absolute -top-2.5 left-4 hd-pill hd-pill--inbound !bg-[var(--hd-accent)] !text-white shadow-md">Current</span>
              )}
              <div className="hd-panel__eyebrow !mt-0">{p.tier === 3 ? "Enterprise" : `Tier ${p.tier}`}</div>
              <div className="text-[20px] font-bold tracking-tight mt-1">{p.name}</div>
              <div className="text-[12px] text-[var(--hd-text-muted)] mb-3">{p.tagline}</div>
              <div className="text-[26px] font-bold tracking-tight tabular-nums">
                {p.contact_sales ? "Custom" : `${fmt(p.amount_minor, cur)}`}
                {!p.contact_sales && <span className="text-[11px] font-medium text-[var(--hd-text-subtle)] ml-1">/ seat / mo</span>}
              </div>
              <ul className="text-[12px] mt-3 space-y-1.5 flex-1">
                {p.features.map((f) => <li key={f} className="flex items-start gap-1.5"><CheckCircle size={12} weight="fill" className="text-emerald-500 mt-0.5 shrink-0"/> {f}</li>)}
              </ul>
              <div className="mt-4">
                {p.contact_sales ? (
                  <a href="mailto:sales@hellobyflow.com?subject=Enterprise%20plan%20enquiry" className="hd-btn hd-btn--secondary w-full justify-center" data-testid={`contact-${p.key}`}><PaperPlaneTilt size={12}/> Contact sales</a>
                ) : isCurrent ? (
                  <button disabled className="hd-btn hd-btn--ghost w-full justify-center opacity-70" data-testid={`current-${p.key}`}>Current plan</button>
                ) : sub ? (
                  <button onClick={() => change(p.key)} disabled={busy} className="hd-btn hd-btn--primary w-full justify-center" data-testid={`switch-${p.key}`}>Switch to {p.name}</button>
                ) : (
                  <button onClick={() => startCheckout(p.key, 1)} disabled={busy || !customer.customer_id} className="hd-btn hd-btn--primary w-full justify-center" data-testid={`buy-${p.key}`}>{busy ? "Starting…" : `Start ${p.name}`}</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {!customer.customer_id && <div className="mt-4 text-[12px] text-amber-700 inline-flex items-center gap-1.5"><Warning size={12}/> Save your billing identity above to unlock checkout.</div>}
    </div>
  );
}
